import React, { useEffect } from 'react';
import Phaser from 'phaser';
import MainMenu from '../scenes/MainMenu';
import GameScene from '../scenes/GameScene';
import WellDoneScene from '../scenes/WellDoneScene';
import FailScene from '../scenes/FailScene';
import GrayScalePostFx from 'phaser3-rex-plugins/plugins/grayscalepipeline.js';

const Game: React.FC = () => {
  useEffect(() => {
    const fragmentShader = `
      precision mediump float;
      uniform float time;
      uniform vec2 resolution;
      uniform sampler2D uMainSampler;
      varying vec2 outTexCoord;

      void main() {
        vec2 uv = outTexCoord;
        
        float scanline = sin(uv.y * resolution.y * 2.0) * 0.02;
        
        float distortion = sin(time * 10.0 + uv.y * 3.0) * 0.001;
        uv.x += distortion;
        
        vec4 color = texture2D(uMainSampler, uv);
        
        color.rgb += scanline;
        
        float vignette = 1.0 - length((uv - 0.5) * 0.5);
        vignette = smoothstep(0.0, 0.25, vignette);
        color.rgb *= vignette;

        gl_FragColor = color;
      }
    `;

    class CRTPipeline extends Phaser.Renderer.WebGL.Pipelines.PostFXPipeline {
      constructor(game: Phaser.Game) {
        super({
          game,
          renderTarget: true,
          fragShader: fragmentShader
        });
      }

      onPreRender() {
        this.set1f('time', this.game.loop.time / 1000);
        this.set2f('resolution', this.renderer.width, this.renderer.height);
      }
    }

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.WEBGL,
      parent: 'game-container',
      width: 320,
      height: 180,
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { x: 0, y: 0 },
          debug: false
        }
      },
      scene: [MainMenu, GameScene, WellDoneScene, FailScene],
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: 320,
        height: 180
      },
      render: {
        pixelArt: true,
        antialias: false,
        roundPixels: true
      },
      callbacks: {
        postBoot: (game) => {
          if (game.renderer instanceof Phaser.Renderer.WebGL.WebGLRenderer) {
            game.renderer.pipelines.addPostPipeline('CRT', CRTPipeline);
          }
        }
      },
      pipeline: {
        "GrayScale": GrayScalePostFx
      }
    };

    const game = new Phaser.Game(config);

    const scenes = [MainMenu, GameScene, WellDoneScene, FailScene];
    scenes.forEach(SceneClass => {
      const originalCreate = SceneClass.prototype.create;
      SceneClass.prototype.create = function() {
        if (originalCreate) {
          originalCreate.call(this);
        }
        const isCRTEnabled = localStorage.getItem('crtEffect') !== 'false';
        if (isCRTEnabled) {
          this.cameras.main.setPostPipeline('CRT');
        } else {
          this.cameras.main.resetPostPipeline();
        }
      };
    });

    return () => {
      game.destroy(true);
    };
  }, []);

  return <div id="game-container"></div>;
};

export default Game;
